import { defer, mergeMap, Observable, of, Subject, Subscriber, take, throwError } from 'rxjs';

type IdleResult = { ok: true } | { ok: false; error: unknown };
type Entry<T> = {
  work: () => Observable<T>;
  subscriber: Subscriber<T>;
  hasValue: boolean;
  value?: T;
};

/** Keep a sent mutation running, with only the latest pending caller retained. */
export class SerializedRequestQueue<T> {
  private active?: Entry<T>;
  private pending?: Entry<T>;
  private busy = false;
  private readonly idle = new Subject<IdleResult>();

  constructor(
    private readonly onPendingCount: (count: number) => void,
    private readonly canContinueAfterError: (error: unknown) => boolean,
  ) {}

  run(work: () => Observable<T>): Observable<T> {
    return new Observable((subscriber) => {
      const entry: Entry<T> = { work, subscriber, hasValue: false };
      const replaced = this.pending;
      this.pending = entry;
      replaced?.subscriber.complete();
      this.reportState();
      this.startNext();
      return () => {
        if (this.pending === entry) {
          this.pending = undefined;
          this.reportState();
        }
      };
    });
  }

  whenIdle(): Observable<void> {
    return defer(() =>
      this.busy
        ? this.idle.pipe(
            take(1),
            mergeMap((result) => (result.ok ? of(undefined) : throwError(() => result.error))),
          )
        : of(undefined),
    );
  }

  private startNext() {
    if (this.active) return;
    const entry = this.pending;
    if (!entry) {
      this.reportState();
      return;
    }
    this.active = entry;
    this.pending = undefined;
    this.reportState();
    defer(entry.work).subscribe({
      next: (value) => {
        entry.value = value;
        entry.hasValue = true;
      },
      complete: () => this.finish(entry),
      error: (error) => this.finish(entry, error, true),
    });
  }

  private finish(entry: Entry<T>, error?: unknown, failed = false) {
    if (this.active !== entry) return;
    if (failed && !this.canContinueAfterError(error)) {
      const latest = this.pending ?? entry;
      this.active = undefined;
      this.pending = undefined;
      this.reportState({ ok: false, error });
      if (latest !== entry) entry.subscriber.complete();
      latest.subscriber.error(error);
      return;
    }

    const superseded = this.pending !== undefined;
    this.active = undefined;
    this.reportState();
    if (!superseded && !this.active && !this.pending) {
      if (failed) entry.subscriber.error(error);
      else if (entry.hasValue) entry.subscriber.next(entry.value!);
    }
    entry.subscriber.complete();
    this.startNext();
  }

  private reportState(result: IdleResult = { ok: true }) {
    const wasBusy = this.busy;
    const count = Number(!!this.active) + Number(!!this.pending);
    this.busy = count > 0;
    this.onPendingCount(count);
    if (wasBusy && !this.busy) this.idle.next(result);
  }
}
